import * as React from 'react';
import { styled } from '@mui/material/styles';
import FormGroup, { FormGroupProps } from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch, { SwitchProps } from '@mui/material/Switch';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Typography } from '@mui/material';

type SelectedFormGroupProps = Pick<FormGroupProps, 'id' | 'className'>; // Only include the specific props you need

export interface ISwitchProps extends SelectedFormGroupProps {
    switchProps?: SwitchProps;
    label?: string;
    style?: React.CSSProperties;
    labelPlacement?: 'bottom' | 'top' | 'start' | 'end';
    showIcon?: boolean;
}

interface IOSSwitchProps extends SwitchProps {
    showIcon?: boolean;
    backgroundcolor?: string;
}

const IOSSwitch = styled(({ showIcon, ...props }: IOSSwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme, showIcon, ...props }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& .MuiSwitch-thumb:before': showIcon
                ? {
                      content: '""',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(
                          `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.32031 11.6835C3.32031 16.6541 7.34975 20.6835 12.3203 20.6835C16.1075 20.6835 19.3483 18.3443 20.6768 15.032C19.6402 15.4486 18.5059 15.6834 17.3203 15.6834C12.3497 15.6834 8.32031 11.654 8.32031 6.68342C8.32031 5.50338 8.55165 4.36259 8.96453 3.32996C5.65605 4.66028 3.32031 7.89912 3.32031 11.6835Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" stroke-linejoin="round"/></svg>`,
                      )}')`,
                  }
                : {},
            '& + .MuiSwitch-track': {
                backgroundColor: props.backgroundcolor || 'rgba(251, 188, 85, 1)',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
        '&:before': showIcon
            ? {
                  content: '""',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(
                      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Environment / Sun"><path id="Vector" d="M12 4V2M12 20V22M6.41421 6.41421L5 5M17.728 17.728L19.1422 19.1422M4 12H2M20 12H22M17.7285 6.41421L19.1427 5M6.4147 17.728L5.00049 19.1422M12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17Z" stroke="#000000" strokeWidth="2" strokeLinecap="round" stroke-linejoin="round"/></g></svg>`,
                  )}')`,
              }
            : {},
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#929292ff' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

export const CustomizedSwitches: React.FC<ISwitchProps> = ({
    switchProps,
    label,
    style,
    labelPlacement,
    showIcon = false,
    ...props
}) => {
    const { themeStyle } = useThemeContext();
    return (
        <FormGroup {...props}>
            <FormControlLabel
                control={
                    <IOSSwitch
                        sx={{ ml: 2, mr: 1 }}
                        showIcon={showIcon}
                        {...switchProps}
                        backgroundcolor={theme[themeStyle].highlight}
                    />
                }
                label={
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                            opacity: switchProps?.disabled ? 0.3 : 1,
                        }}>
                        {label}
                    </Typography>
                }
                style={{ color: theme[themeStyle].tag_white, ...style }}
                labelPlacement={labelPlacement}
            />
        </FormGroup>
    );
};
